import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectFormsService } from "@components/project-forms/project-forms.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { TemplateGoodsRecipientDto } from "@domain/project/template/property/project/details/goods-recipient/template-goods-recipient.dto";
import { UserService } from "@domain/user/user.service";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { FormFieldComponent } from "@odx/angular/components/form-field";
import { map, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-goods-recipient-form",
  templateUrl: "./goods-recipient-form.component.html",
  styleUrls: ["./goods-recipient-form.component.scss"],
  imports: [AreaHeaderComponent, FormFieldComponent, UiKitModule, ReactiveFormsModule],
  standalone: true,
})
export class GoodsRecipientFormComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private projectFormsService = inject(ProjectFormsService);
  private projectService = inject(ProjectService);

  isReadonly: InputSignal<boolean> = input.required<boolean>();
  @Input() template!: TemplateGoodsRecipientDto;
  @Output() goodsRecipientFormEmitter$ = new EventEmitter<FormGroup>();
  goodsRecipientForm!: FormGroup;

  projectSignal = input<ScopedProject | undefined>(undefined);

  @ViewChild("submitButton")
  submitButton?: ElementRef;
  private readonly _destroying = new Subject<void>();
  private isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
  isReadOnlyUser = toSignal(this.isReadOnlyUser$);
  isWriteable = signal<boolean>(false);

  constructor() {
    effect(() => {
      const project = this.projectSignal();
      if (project === undefined || project === null) return;
      this.initForm(project);
    });
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    effect(() => {
      this.isReadonly();
      this.isReadOnlyUser();
      const result = !this.isReadOnlyUser() && !this.isReadonly();
      this.isWriteable.update(() => result);
    });

    this.goodsRecipientForm = this.formBuilder.group({
      information: [],
    });

    this.projectFormsService.projectReset$.pipe(takeUntil(this._destroying)).subscribe((project) => {
      this.goodsRecipientForm.reset();
      this.initForm(project);
    });

    this.goodsRecipientForm.statusChanges
      .pipe(takeUntil(this._destroying))
      .subscribe(() => this.goodsRecipientFormEmitter$.emit(this.goodsRecipientForm));
  }

  ngOnInit(): void {
    if (this.template?.information?.required) {
      this.goodsRecipientForm.get("information")?.setValidators(Validators.required);
      this.goodsRecipientForm.get("information")?.updateValueAndValidity();
    }
    this.goodsRecipientFormEmitter$.emit(this.goodsRecipientForm);
  }

  ngOnDestroy(): void {
    this._destroying.next();
    this._destroying.complete();
  }

  initForm(project: ScopedProject) {
    this.goodsRecipientForm.patchValue({
      information: project.goodsRecipient?.information,
    });
  }

  triggerSubmit() {
    this.submitButton?.nativeElement.click();
  }

  protected readonly navigator = navigator;
}
